"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchAuditLog } from "../../../services/billingService";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { formatBillingDate } from "../../../utils/billingFormat";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const formatAuditFieldValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string" && !Number.isNaN(Date.parse(value)) && /\d{4}-\d{2}-\d{2}/.test(value)) {
    return formatBillingDate(value);
  }
  return String(value);
};

const formatAuditDetails = (log) => {
  const { fromValue, toValue } = log;
  if (fromValue === null && toValue === null) return "-";

  if (fromValue === null && toValue && typeof toValue === "object") {
    return Object.entries(toValue)
      .map(([key, value]) => `${key}: ${formatAuditFieldValue(value)}`)
      .join(", ");
  }

  if (toValue && typeof toValue === "object" && fromValue && typeof fromValue === "object") {
    const keys = new Set([...Object.keys(fromValue), ...Object.keys(toValue)]);
    return Array.from(keys)
      .map((key) => `${key}: ${formatAuditFieldValue(fromValue[key])} → ${formatAuditFieldValue(toValue[key])}`)
      .join(", ");
  }

  return `${formatAuditFieldValue(fromValue)} → ${formatAuditFieldValue(toValue)}`;
};

const SchoolAuditLog = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAuditLog(schoolId)
      .then((res) => setLogs(res.data || []))
      .catch((err) => setError(extractApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [schoolId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Billing Audit Log</h1>
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}/billing`)}>
          Back to Billing
        </Button>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No billing actions recorded yet for this customer.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log._id}>
                  <TableCell className="font-medium">{log.action}</TableCell>
                  <TableCell className="text-sm text-gray-600">{formatAuditDetails(log)}</TableCell>
                  <TableCell>{log.actor?.email || log.actor?.type || "-"}</TableCell>
                  <TableCell>{log.reason || "-"}</TableCell>
                  <TableCell>{formatBillingDate(log.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default SchoolAuditLog;
